'use client';

import { forwardRef } from 'react';
import { cn } from '@/core/utils/cn';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const errorId = textareaId ? `${textareaId}-error` : undefined;
    const hintId = textareaId ? `${textareaId}-hint` : undefined;
    const describedBy = error ? errorId : hint ? hintId : undefined;
    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={textareaId} className="block text-[12px] font-medium text-hms-textSub">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'w-full px-3 py-2 text-[13px] text-hms-text bg-hms-bg border border-hms-border rounded-lg',
            'placeholder:text-hms-textDim',
            'focus:outline-none focus:ring-2 focus:ring-hms-gold/30 focus:border-hms-gold/50',
            'transition-colors duration-150 resize-y min-h-[80px]',
            error && 'border-hms-red/50 focus:ring-hms-red/30',
            props.disabled && 'opacity-50 cursor-not-allowed',
            className
          )}
          {...props}
        />
        {error && (
          <p id={errorId} role="alert" className="text-[11px] text-hms-red">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="text-[11px] text-hms-textDim">
            {hint}
          </p>
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';
